(() => {
  "use strict";

  const CONFIG_SELECTOR = "script[data-si-mp-pdp-contract-config]";
  const PROPERTY_PREFIX = "properties[";

  const stringValue = (value) => String(value ?? "").trim();

  const hiddenProperty = (form, propertyName) => {
    const name = `${PROPERTY_PREFIX}${propertyName}]`;
    let input = [...form.elements].find((element) => element?.name === name);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.dataset.siMpPdpProperty = propertyName;
      form.append(input);
    }
    return input;
  };

  const setProperty = (form, name, value) => {
    const input = hiddenProperty(form, name);
    input.value = String(value ?? "");
  };

  const initContract = (configNode) => {
    if (configNode.dataset.siMpPdpReady === "1") return;
    configNode.dataset.siMpPdpReady = "1";

    const form = configNode.closest("form[data-type='add-to-cart-form']") || configNode.closest("form");
    if (!form) return;

    let config;
    try {
      config = JSON.parse(configNode.textContent || "{}");
    } catch {
      config = null;
    }

    const variantInput = form.querySelector("input.product-variant-id[name='id'], input[name='id']");
    const addButton = form.querySelector("button[name='add']");
    const errorNode = form.querySelector("[data-si-mp-pdp-contract-error]");

    const fail = (message) => {
      if (addButton) {
        addButton.disabled = true;
        addButton.dataset.siMpContractDisabled = "1";
      }
      if (errorNode) {
        errorNode.textContent = message;
        errorNode.hidden = false;
      }
      return false;
    };

    const clearFailure = () => {
      if (errorNode) {
        errorNode.textContent = "";
        errorNode.hidden = true;
      }
      if (addButton?.dataset.siMpContractDisabled === "1") {
        delete addButton.dataset.siMpContractDisabled;
        if (!variantInput?.disabled) addButton.disabled = false;
      }
    };

    const sync = () => {
      if (!config || config.contractVersion !== "si-mp-cart-contract-v1") {
        return fail("This personalised product is missing its production contract. Please contact Spirit Ink.");
      }

      const requiredProductFields = [
        "productKey",
        "productId",
        "colourId",
        "colourLabel",
        "designId",
        "designHash",
        "artworkHash",
        "artworkGeometryVersion",
        "sessionId",
        "iteration",
        "approvedPreviewUrl",
      ];
      if (requiredProductFields.some((field) => !stringValue(config[field]))) {
        return fail("This personalised product is missing required production data. Please contact Spirit Ink.");
      }

      let previewUrl;
      let artworkUrl;
      try {
        previewUrl = new URL(config.approvedPreviewUrl);
        artworkUrl = new URL(previewUrl.searchParams.get("artwork") || "");
      } catch {
        return fail("This personalised product has an invalid artwork reference. Please contact Spirit Ink.");
      }
      if (
        previewUrl.protocol !== "https:"
        || artworkUrl.protocol !== "https:"
        || previewUrl.searchParams.get("product_id") !== config.productId
        || previewUrl.searchParams.get("colour_id") !== config.colourId
      ) {
        return fail("This personalised product has an invalid production reference. Please contact Spirit Ink.");
      }

      const offsetX = Number(previewUrl.searchParams.get("offset_x"));
      const offsetY = Number(previewUrl.searchParams.get("offset_y"));
      const scale = Number(previewUrl.searchParams.get("scale"));
      if (
        !Number.isFinite(offsetX) || offsetX < -1 || offsetX > 1
        || !Number.isFinite(offsetY) || offsetY < -1 || offsetY > 1
        || !Number.isFinite(scale) || scale < 0.55 || scale > 1
      ) {
        return fail("This personalised product has invalid placement data. Please contact Spirit Ink.");
      }

      const variantId = stringValue(variantInput?.value);
      const variant = config.variants?.[variantId];
      const optionKey = stringValue(variant?.optionKey);
      const templateId = stringValue(variant?.templateId);
      if (!variantId || !optionKey || !templateId) {
        return fail("This size is not mapped for personalised production. Please choose another size or contact Spirit Ink.");
      }

      setProperty(form, "Personalised design", "Yes");
      setProperty(form, "Product colour", config.colourLabel);
      setProperty(form, "_magic_preview_artwork_url", artworkUrl.href);
      setProperty(form, "_magic_preview_session_id", config.sessionId);
      setProperty(form, "_magic_preview_iteration", config.iteration);
      setProperty(form, "_magic_preview_offset_x", offsetX.toFixed(3));
      setProperty(form, "_magic_preview_offset_y", offsetY.toFixed(3));
      setProperty(form, "_magic_preview_scale", scale.toFixed(3));
      setProperty(form, "_magic_preview_template_id", templateId);
      setProperty(form, "_magic_preview_product_key", config.productKey);
      setProperty(form, "_magic_preview_product_id", config.productId);
      setProperty(form, "_magic_preview_colour_id", config.colourId);
      setProperty(form, "_magic_preview_option_key", optionKey);
      setProperty(form, "_magic_preview_design_id", config.designId);
      setProperty(form, "_magic_preview_design_hash", config.designHash);
      setProperty(form, "_magic_preview_artwork_hash", config.artworkHash);
      setProperty(form, "_magic_preview_artwork_geometry_version", config.artworkGeometryVersion);
      setProperty(form, "_magic_preview_approved_preview_url", config.approvedPreviewUrl);

      clearFailure();
      return true;
    };

    variantInput?.addEventListener("change", sync);
    form.addEventListener("change", (event) => {
      if (event.target !== variantInput) window.setTimeout(sync, 0);
    });
    form.addEventListener("submit", (event) => {
      if (!sync()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    sync();
  };

  const start = () => document.querySelectorAll(CONFIG_SELECTOR).forEach(initContract);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
